/**
 * This module is to provide a communication layer between the web and the native app
 *
 * They way to do it is by creating an img DOM element in the body which is going to use an
 * special src that will notify the native app to run a method exposed globaly that will get the
 * array containig the queue of operations to be perform in the app.
 *
 * @module app/components/QuickBets
 */
import jQuery from 'jquery';

/**
 * Array used to store all the messages pending to be taken by the native app.
 *
 * @type {Array}
 *
 * @private
 */
var messageQueue = [];


/**
 * This queue is used to store not notified new items.
 * We do this as we dont know how long the app will take to collect the items
 * So this queue is flush and moved to the message queue everytime we notify
 *
 * @type {Array}
 *
 * @private
 */
var temporalQueue = [];

/**
 * protocol used in the src request in the tunnel
 *
 * @type {String}
 *
 * @private
 */
var scheme = 'sbkscheme';

/**
 * Message used to compose the url to notify the native app
 *
 * @type {String}
 *
 * @private
 */
var notificationMessage = '__SBK_MESSAGE__';

/**
 * Message bus subscription topic
 *
 * @type {String}
 *
 * @private
 */
var messageBusTopic = 'NativeAppBridge.sendMessage';

/**
 * Defines a query parameter not to get the requests repeated or cached
 *
 * @type {Integer}
 *
 * @private
 */
var messageNumber = 0;

/**
 * Miliseconds to wait before releasing the current lock in case the onload callback fails
 *
 * @type {Integer}
 *
 * @private
 */
var nativeAppResponseTime = 1500;

/**
 * jQuery selector containing the DOM element which is used as tunnel by changing the src
 * into the request to be made
 *
 * @type {Object}
 *
 * @private
 */
var DOMTunnel = null;

/**
 * Flag to determinate if we are in sending a request through the tunnel at the moment
 *
 * @type {Boolean}
 *
 * @private
 */
var sending = false;

/**
 * Object containing all the possible operations received by the messagebus to be perform
 * Any operation not included in this object will be skipped.
 *
 * @type {Object}
 *
 * @private
 */
var operations = {

    /**
     * Composes the action to be added to the queue to be fetched by the native app and notifies
     * the module to publish it.
     *
     * @param   {Object}    data [description]
     *
     * @returns {void}
     *
     * @private
     */
    quickSingleOpenend: function (data) {

        sendMessage({
            action: 'addLeg',
            data: data,
        });
    },
};

/**
 * Creates the communication tunnel which in this case is done by an image.
 * The notifications will be sent by using the src of the image.
 *
 * @returns {void}
 *
 * @public
 */
var createTunnel = function createDOMElement() {

    DOMTunnel = jQuery('<iframe width="0" height="0" style="display:none;">').attr('src', '').appendTo('body');

};

/**
 * Exposes the send and fetch actions in to the browser global scope
 *
 * @returns {void}
 *
 * @private
 */
var exposeBridge = function exposeBridge() {

    window.sportsbookNativeBridge = {
        send: sendMessage,
        fetchQueue: fetchQueue,
    };

};

/**
 * Returns an stringified version of the queue and then clears it.
 * The native app is supposed to use this function which is being exposed
 * in the window object through the bridge
 *
 * @returns {String}  Stringify Array of objects contining operations
 *
 * @private
 */
var fetchQueue = function fetchQueue() {

    var queue = JSON.stringify(messageQueue);

    messageQueue = [];

    return queue;
};

/**
 * Adds a message to the queue and sends everything unless something is ongoing already.
 * Anyway once the current one finishes a re check will be launch
 *
 * @param  {Object} message Object in the protocol format to communicate to native app.
 *
 * @return {void}
 *
 * @private
 */
var sendMessage = function sendMessage(message) {

    temporalQueue.push(message);

    if (!sending) {

        publishIntoTunnel();
    }

};

/**
 * Sets a sending flag to true so we dont send anything else till the current message is being sent
 *
 * @returns {void}
 *
 * @private
 */
var closeSendingLock = function closeSendingLock() {

    sending = true;
};

/**
 * This method is to be trigger once the message is being sent using the tunnel and
 * then checking if there are others waiting to be send
 *
 * @returns     {void}
 *
 * @private
 */
var publishIntoTunnel = function sendByTunnel() {

    closeSendingLock();

    DOMTunnel
        .attr('src', scheme + '://' + notificationMessage + '?mn=' + messageNumber++);

    // extend the message queue with all the new messages notified
    messageQueue = messageQueue.concat(messageQueue, temporalQueue);

    // clear the temporal queue
    temporalQueue = [];

    //in case the onload of the img is not working
    window.setTimeout(onMessageSent, nativeAppResponseTime);

};

/**
 * This method is to be trigger once the message is being sent using the tunnel and
 * then checking if there are others waiting to be sent
 *
 * @returns     {void}
 *
 * @private
 */
var onMessageSent = function onMessageSent() {

    sending = false;

    // if we got new stuff while the previous was being sent
    if (temporalQueue.length > 0) {

        publishIntoTunnel();
    }

};

/**
 * This method will try to stract an operation received from the message bus and a data attached
 * to trigger an action to communicate with the native app.
 *
 * @param       {Object}    message  Communication key
 * @param       {Object}    data     Object containing operation + data keys
 *
 * @returns     {void}
 *
 * @private
 */
var processMessage = function processMessage(message, data) {

    if (data && data.operation && operations.hasOwnProperty(data.operation)) {

        operations[data.operation](data.data);
    }
};

/**
 * This will make this module start listening to the message bus in order to receive actions to be
 * sent to the native app through the tunnel.
 *
 * @returns {void}
 *
 * @private
 */
var subscribeToMessageBus = function startListening() {

    window.WH.messageBus.subscribe(messageBusTopic, processMessage);
};

/**
 * Public API.
 *
 * @type {Object}
 *
 * @public
 */
var api = {

    /**
     * This function initializes the communication with the native app
     *
     * @returns {void}
     *
     * @public
     */
    init: function init() {

        createTunnel();
        exposeBridge();
        subscribeToMessageBus();

    },

};

export default api;
